import { Container } from 'pixi.js';
import type { PlacedFurnitureView } from '@duck/contracts';
import { buildDecorItem, isFloorDecor } from './decor-catalog';
import type { ThemeArt } from './theme-art';

/**
 * Renders the room's PLACED FURNITURE (Furniture Store) as a reactive layer, mirroring {@link DiscoZoneLayer}:
 * the authoritative room state carries a set of placed pieces (`furniture`, keyed by id), and THIS layer
 * reconciles the on-screen pieces to it — building a Pixi body for a new id, removing one whose id is gone.
 * Each body is built from the SAME {@link buildDecorItem} catalog the editor + the baked office map use, so a
 * bought piece looks identical to an editor-placed one (ТЗ §2 — reuse, don't duplicate).
 *
 * PERFORMANCE: pieces are built ONCE on first appearance and reused; {@link reconcile} only diffs the id set
 * (no per-frame allocation, no full rebuild while furniture is steady). Rugs (floor decor) sort below other
 * pieces; everything else sorts by `y` so nearer furniture overlaps correctly.
 *
 * ACCESSIBILITY/Art: lives BELOW the ducks/labels (added by the engine alongside the highlight layer), so
 * furniture sits on the floor and never occludes the foreground. Code-authored primitives only (ADR-0010).
 */
export class FurnitureLayer {
  /** Added by the engine BELOW the ducks; world-space (pieces are drawn at absolute room coordinates). */
  readonly root = new Container();

  /** Live piece bodies keyed by furniture id. */
  private readonly pieces = new Map<string, Container>();
  /** The ThemeArt the current bodies were built with — a theme swap rebuilds them all to re-skin. */
  private art: ThemeArt | null = null;
  /** A piece currently being CARRIED (move mode) — hidden in place so only the cursor ghost shows. */
  private hiddenId: string | null = null;

  constructor() {
    this.root.sortableChildren = true;
  }

  /**
   * Reconcile the rendered pieces to the authoritative set. Adds bodies for new ids, removes bodies for
   * vanished ids, and leaves unchanged pieces untouched. A new `art` (theme swap) rebuilds every body so
   * the furniture re-skins with the world. Cheap: O(current + next) id comparison, no work when steady.
   */
  reconcile(furniture: readonly PlacedFurnitureView[], art: ThemeArt): void {
    if (art !== this.art) {
      // Theme changed → rebuild all bodies with the new art (rare; only on an owner's theme switch).
      this.art = art;
      for (const body of this.pieces.values()) body.destroy({ children: true });
      this.pieces.clear();
      this.root.removeChildren();
    }

    const next = new Set<string>();
    for (const p of furniture) {
      next.add(p.id);
      const existing = this.pieces.get(p.id);
      if (existing) {
        // A piece can now MOVE / ROTATE (context-menu "Take"/"Rotate"), so keep its transform in sync with
        // authoritative state. Cheap idempotent assignments — only the transform changes, never the body.
        existing.x = p.x;
        existing.y = p.y;
        existing.rotation = p.rotation ?? 0;
        if (p.scale !== undefined) existing.scale.set(p.scale);
        existing.zIndex = isFloorDecor(p.kind) ? -10000 : p.y;
        existing.visible = this.hiddenId !== p.id;
        continue;
      }
      const body = buildDecorItem(p, art);
      // Rugs lie flat under everything; other pieces sort by y so closer furniture draws in front.
      body.zIndex = isFloorDecor(p.kind) ? -10000 : p.y;
      body.visible = this.hiddenId !== p.id;
      this.pieces.set(p.id, body);
      this.root.addChild(body);
    }

    // Remove pieces no longer present (removed by their placer or the host).
    for (const [id, body] of this.pieces) {
      if (next.has(id)) continue;
      this.root.removeChild(body);
      body.destroy({ children: true });
      this.pieces.delete(id);
    }
  }

  /** Hide (or, with null, unhide) one piece while it is being CARRIED in move mode, so only the cursor
   * ghost is visible. Applied immediately to the live body; the next reconcile keeps it consistent. */
  setHidden(id: string | null): void {
    if (this.hiddenId && this.hiddenId !== id) {
      const prev = this.pieces.get(this.hiddenId);
      if (prev) prev.visible = true;
    }
    this.hiddenId = id;
    if (id) {
      const body = this.pieces.get(id);
      if (body) body.visible = false;
    }
  }

  /** Release all bodies (engine teardown). */
  destroy(): void {
    for (const body of this.pieces.values()) body.destroy({ children: true });
    this.pieces.clear();
    this.root.destroy({ children: true });
  }
}
